import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  CAROUSEL_IMAGE_HINT,
  mergeCarouselDetailsWithLanguages,
  stripCarouselDetails,
} from './carouselConstants';

const EMPTY_FORM = {
  sort: 1,
  details: [],
};

export default function CarouselEditModal({ open, carouselId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(carouselId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const langList = await adminApi.newsLanguages();
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        if (carouselId) {
          const data = await adminApi.getCarousel(carouselId);
          if (cancelled) {
            return;
          }
          setForm({
            sort: data.sort ?? 1,
            details: mergeCarouselDetailsWithLanguages(langList, data.details),
          });
        } else {
          setForm({
            sort: 1,
            details: mergeCarouselDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, carouselId]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  async function handleImageUpload(index, event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const url = await adminApi.uploadOssImage(file);
      updateDetail(index, 'image', url);
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      event.target.value = '';
    }
  }

  function handleReset() {
    setError('');
    if (isEdit) {
      setForm((prev) => ({
        ...prev,
        details: prev.details.map((detail) => ({ ...detail, image: '' })),
      }));
      return;
    }
    setForm({
      sort: 1,
      details: mergeCarouselDetailsWithLanguages(languages, []),
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const details = stripCarouselDetails(form.details).filter((d) => d.image?.trim());
    if (!details.length) {
      setError('至少上傳一個語系的輪播圖');
      return;
    }
    const payload = {
      sort: Number(form.sort),
      details,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateCarousel({ id: carouselId, ...payload });
      } else {
        await adminApi.createCarousel(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '修改輪播圖' : '新增'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p className="table-empty">載入中…</p>
        ) : (
          <form className="modal-panel__body carousel-edit-form" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <div className="form-field">
              <label htmlFor="carouselSort">
                <span className="form-label-required">顯示順序</span>
              </label>
              <input
                id="carouselSort"
                type="number"
                min="1"
                value={form.sort}
                onChange={(e) => setForm((p) => ({ ...p, sort: e.target.value }))}
                required
              />
            </div>

            {form.details.map((detail, index) => (
              <section key={detail.lang} className="carousel-lang-section">
                <div className="form-field">
                  <span className="form-label">
                    輪播圖（{detail._label || detail.lang}）
                  </span>
                  <div className="carousel-image-upload">
                    {detail.image ? (
                      <img src={resolveAdminMediaUrl(detail.image)} alt="" className="carousel-thumb-preview" />
                    ) : (
                      <div className="carousel-thumb-placeholder">未設定</div>
                    )}
                    <label className="btn btn-success">
                      上傳輪播圖（{CAROUSEL_IMAGE_HINT}）（{detail._label || detail.lang}）
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleImageUpload(index, e)}
                      />
                    </label>
                    {detail.image ? (
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={() => updateDetail(index, 'image', '')}
                      >
                        清除
                      </button>
                    ) : null}
                  </div>
                </div>
              </section>
            ))}

            <div className="modal-panel__footer admin-modal__footer--center">
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? '提交中…' : '提交'}
              </button>
              <button type="button" className="btn btn-warning" onClick={handleReset} disabled={submitting}>
                重置
              </button>
              <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
                關閉
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
